/**
 * Per-admin theme system.
 *
 * Each admin can pick:
 *   - mode:   'light' | 'dark'
 *   - accent: 'emerald' | 'violet' | 'cyan' | 'amber' | 'rose' | 'indigo'
 *
 * Persisted in localStorage scoped to the user id (`jtr-admin-theme-<uid>`)
 * so two admins on the same browser keep separate preferences.
 *
 * Applied two ways:
 *   1. Adds/removes the `dark` class on <html> for Tailwind `dark:` variants.
 *   2. Sets `data-admin-accent="<name>"` on <html> so CSS variables in
 *      index.css can repaint accent colors across the admin dashboard.
 */

import { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { useAuth } from '@/lib/auth';

const ACCENTS = ['emerald', 'violet', 'cyan', 'amber', 'rose', 'indigo'];
const MODES = ['light', 'dark'];

const DEFAULTS = { mode: 'dark', accent: 'emerald' };

function storageKey(uid) {
  return `jtr-admin-theme-${uid || 'anon'}`;
}

function readStored(uid) {
  try {
    const raw = localStorage.getItem(storageKey(uid));
    if (!raw) return DEFAULTS;
    const v = JSON.parse(raw);
    return {
      mode:   MODES.includes(v.mode) ? v.mode : DEFAULTS.mode,
      accent: ACCENTS.includes(v.accent) ? v.accent : DEFAULTS.accent,
    };
  } catch {
    return DEFAULTS;
  }
}

const Ctx = createContext(null);

export function AdminThemeProvider({ children }) {
  const { user } = useAuth() || {};
  const uid = user?.id || user?.email || 'anon';

  const [theme, setTheme] = useState(() => readStored(uid));

  // Re-read whenever the logged-in admin changes.
  useEffect(() => { setTheme(readStored(uid)); }, [uid]);

  // Apply to <html> and persist.
  useEffect(() => {
    const html = document.documentElement;
    if (theme.mode === 'dark') html.classList.add('dark');
    else html.classList.remove('dark');
    html.setAttribute('data-admin-accent', theme.accent);
    html.setAttribute('data-admin-mode', theme.mode);
    try { localStorage.setItem(storageKey(uid), JSON.stringify(theme)); } catch { /* ignore */ }
    return () => {
      // do not strip on unmount — public pages should keep current mode
    };
  }, [theme, uid]);

  const setMode   = useCallback((m) => setTheme((t) => ({ ...t, mode:   MODES.includes(m) ? m : t.mode })), []);
  const setAccent = useCallback((a) => setTheme((t) => ({ ...t, accent: ACCENTS.includes(a) ? a : t.accent })), []);
  const toggleMode = useCallback(() => setTheme((t) => ({ ...t, mode: t.mode === 'dark' ? 'light' : 'dark' })), []);
  const reset = useCallback(() => setTheme(DEFAULTS), []);

  const value = useMemo(() => ({
    mode: theme.mode,
    accent: theme.accent,
    accents: ACCENTS,
    modes: MODES,
    setMode, setAccent, toggleMode, reset,
  }), [theme, setMode, setAccent, toggleMode, reset]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAdminTheme() {
  const ctx = useContext(Ctx);
  if (!ctx) {
    // Safe default when used outside the provider (e.g. public pages).
    return {
      mode: 'dark', accent: 'emerald', accents: ACCENTS, modes: MODES,
      setMode: () => {}, setAccent: () => {}, toggleMode: () => {}, reset: () => {},
    };
  }
  return ctx;
}

export const ACCENT_SWATCHES = {
  emerald: { en: 'Emerald', ja: 'エメラルド', hex: '#10b981' },
  violet:  { en: 'Violet',  ja: 'バイオレット', hex: '#8b5cf6' },
  cyan:    { en: 'Cyan',    ja: 'シアン',     hex: '#06b6d4' },
  amber:   { en: 'Amber',   ja: 'アンバー',   hex: '#f59e0b' },
  rose:    { en: 'Rose',    ja: 'ローズ',     hex: '#f43f5e' },
  indigo:  { en: 'Indigo',  ja: 'インディゴ', hex: '#6366f1' },
};
